import { Box, Collapse, Group, Paper, Table, UnstyledButton } from "@mantine/core"
import { DatePickerInput } from "@mantine/dates"
import { CalendarIcon, CaretRightIcon } from "@phosphor-icons/react"
import dayjs from "dayjs"
import Highcharts from "highcharts"
import "highcharts/highcharts-more"
import HighchartsReact from "highcharts-react-official"
import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import RenderText from "../../../components/ui/render-text"
import { SegmentedTabs } from "../../../components/ui/segmented-tabs"
import { IDashboard } from "../../../models/Dashboard"
import { currencyFormat } from "../../../utils"
import { CardHeader } from "./CardHeader"
import classes from "../real.module.css"

// «Сарын явц»: /ord/dashboard/main sends byInterval as running totals from the
// range start (de-order-service logics/dashboard/_interval.ts), one point per
// day up to today. Drawn as two cumulative lines on ONE axis — ordered and
// delivered — with the band between them = ordered but not yet delivered.
// Amount and count never share the plot: «Дүн | Тоо» swaps the whole chart,
// and the tooltip always shows both figures for each line.

type Mode = "amount" | "count"
type IntervalRow = NonNullable<IDashboard["byInterval"]>[number]

interface Props {
  byInterval: IDashboard["byInterval"] | undefined
  isBuyer: boolean
  startDate: string | null
  endDate: string | null
  onStartDateChange: (next: string | null) => void
  onEndDateChange: (next: string | null) => void
}

// Validated pair (dataviz validate_palette: lightness, chroma, CVD ΔE 25.8, ≥3:1 on white).
const SERIES = {
  ordered: "var(--mantine-color-indigo-7)",
  delivered: "var(--mantine-color-teal-7)",
}
const BAND_OPACITY = 0.12
// RenderText sizes non-amount text to max-content, so a right-aligned count needs auto margin.
const COUNT_CELL = { marginLeft: "auto" }

interface Point {
  day: string
  label: string
  orderAmount: number
  orderCount: number
  deliveredAmount: number
  deliveredCount: number
}

function toPoints(rows: IntervalRow[], startDate: string | null): Point[] {
  return rows.map((row, i) => ({
    // byInterval starts at the range start and has one row per day.
    day: startDate ? dayjs(startDate).add(i, "day").format("YYYY-MM-DD") : row?.date,
    label: row?.date,
    orderAmount: row?.orderAmount || 0,
    orderCount: row?.orderCount || 0,
    deliveredAmount: row?.deliveredAmount || 0,
    deliveredCount: row?.deliveredCount || 0,
  }))
}

export function SalesChartCard({
  byInterval,
  isBuyer,
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
}: Props) {
  const { t } = useTranslation(["dashboard"])
  const [mode, setMode] = useState<Mode>("amount")
  const [tableOpen, setTableOpen] = useState(false)

  const points = useMemo(() => toPoints(byInterval ?? [], startDate), [byInterval, startDate])

  const labels = useMemo(
    () => ({
      ordered: t("dashboard:chart.ordered"),
      delivered: t("dashboard:chart.delivered"),
      undelivered: t("dashboard:chart.undelivered"),
      million: t("dashboard:numberFormat.million"),
      thousand: t("dashboard:numberFormat.thousand"),
      asOf: (day: string) => t("dashboard:chart.asOf", { date: day }),
      orders: (count: number) => t("dashboard:chart.orders", { count }),
    }),
    [t],
  )

  const options = useMemo(() => buildChartOptions(points, mode, labels), [points, mode, labels])

  return (
    <Paper className={classes.card}>
      <CardHeader
        title={t(isBuyer ? "dashboard:purchaseOrderTitle" : "dashboard:salesOrderTitle")}
        helpKey="order.dashboard.section.sales-chart"
        subtitle={t("dashboard:chart.cumulative")}
        right={
          <Group gap={8} wrap="wrap">
            <SegmentedTabs
              value={mode}
              onChange={(v) => setMode(v as Mode)}
              data={[
                { value: "amount", label: t("dashboard:chart.mode.amount") },
                { value: "count", label: t("dashboard:chart.mode.count") },
              ]}
              aria-label={t("dashboard:chart.mode.label")}
            />
            <DatePickerInput
              type="range"
              leftSection={<CalendarIcon size={16} />}
              valueFormat="YYYY-MM-DD"
              value={[startDate ? dayjs(startDate).toDate() : null, endDate ? dayjs(endDate).toDate() : null]}
              onChange={(e) => {
                onStartDateChange(e[0] ? dayjs(e[0]).format("YYYY-MM-DD") : null)
                onEndDateChange(e[1] ? dayjs(e[1]).format("YYYY-MM-DD") : null)
              }}
              aria-label={t("dashboard:chart.range")}
            />
          </Group>
        }
      />
      <Box className={classes.legend}>
        <Box className={classes.legendItem}>
          <Box className={classes.legendLine} style={{ background: SERIES.ordered }} />
          {labels.ordered}
        </Box>
        <Box className={classes.legendItem}>
          <Box className={classes.legendLine} style={{ background: SERIES.delivered }} />
          {labels.delivered}
        </Box>
        <Box className={classes.legendItem}>
          <Box className={classes.legendBand} />
          {labels.undelivered}
        </Box>
      </Box>
      <HighchartsReact highcharts={Highcharts} options={options} />

      <UnstyledButton
        className={classes.chartTableToggle}
        onClick={() => setTableOpen((o) => !o)}
        aria-expanded={tableOpen}
      >
        <CaretRightIcon size={12} weight="fill" className={classes.chartTableCaret} data-open={tableOpen || undefined} />
        {t("dashboard:chart.showTable")}
      </UnstyledButton>
      <Collapse expanded={tableOpen}>
        <Box className={classes.chartTableWrap}>
          <Table>
            <Table.Thead className={classes.tableHead}>
              <Table.Tr>
                <Table.Th>{t("dashboard:chart.date")}</Table.Th>
                <Table.Th ta="right">{t("dashboard:chart.orderAmount")}</Table.Th>
                <Table.Th ta="right">{t("dashboard:chart.orderCount")}</Table.Th>
                <Table.Th ta="right">{t("dashboard:chart.deliveredAmount")}</Table.Th>
                <Table.Th ta="right">{t("dashboard:chart.deliveredOrderCount")}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {points.map((p) => (
                <Table.Tr key={p.day}>
                  <Table.Td><RenderText text={p.day} color="dimmed" /></Table.Td>
                  <Table.Td ta="right"><RenderText text={p.orderAmount} isAmount plainAmount fontWeight={600} /></Table.Td>
                  <Table.Td ta="right"><RenderText text={p.orderCount} style={COUNT_CELL} /></Table.Td>
                  <Table.Td ta="right"><RenderText text={p.deliveredAmount} isAmount plainAmount fontWeight={600} /></Table.Td>
                  <Table.Td ta="right"><RenderText text={p.deliveredCount} style={COUNT_CELL} /></Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Box>
      </Collapse>
    </Paper>
  )
}

interface Labels {
  ordered: string
  delivered: string
  undelivered: string
  million: string
  thousand: string
  asOf: (day: string) => string
  orders: (count: number) => string
}

function axisLabel(value: number, mode: Mode, labels: Labels): string {
  if (mode === "count") return String(value)
  if (value >= 1_000_000) return `${+(value / 1_000_000).toFixed(1)} ${labels.million}`
  if (value >= 1_000) return `${+(value / 1_000).toFixed(0)} ${labels.thousand}`
  return String(value)
}

function tooltipRow(color: string | null, name: string, amount: number, count: number, labels: Labels): string {
  const swatch = color
    ? `<span style="display:inline-block;width:12px;height:2px;border-radius:1px;background:${color};margin-right:6px;vertical-align:middle"></span>`
    : `<span style="display:inline-block;width:10px;height:10px;border-radius:2px;background:${SERIES.ordered};opacity:${BAND_OPACITY * 2};margin-right:6px;vertical-align:middle"></span>`
  return `<tr>
    <td style="padding:2px 12px 2px 0;color:var(--mantine-color-gray-7)">${swatch}${name}</td>
    <td style="padding:2px 0;text-align:right;font-weight:600;color:var(--mantine-color-text)">${currencyFormat(amount)}</td>
    <td style="padding:2px 0 2px 10px;text-align:right;color:var(--mantine-color-gray-7)">${labels.orders(count)}</td>
  </tr>`
}

function buildChartOptions(points: Point[], mode: Mode, labels: Labels): Highcharts.Options {
  const ordered = points.map((p) => (mode === "amount" ? p.orderAmount : p.orderCount))
  const delivered = points.map((p) => (mode === "amount" ? p.deliveredAmount : p.deliveredCount))
  const last = points.length - 1
  // Only today's (the last) point keeps a dot, so the line reads as "where we are now".
  const withEndDot = (values: number[], color: string) =>
    values.map((y, i) => (i === last ? { y, marker: { enabled: true, radius: 4, fillColor: color, lineColor: "var(--mantine-color-body)", lineWidth: 2 } } : y))

  return {
    chart: {
      height: 280,
      style: { fontFamily: "inherit" },
      backgroundColor: "transparent",
      spacing: [8, 4, 8, 0],
    },
    title: { text: "" },
    xAxis: {
      categories: points.map((p) => p.label),
      crosshair: { color: "var(--mantine-color-gray-4)", width: 1 },
      labels: { style: { color: "var(--mantine-color-gray-7)", fontSize: "13px" } },
      lineColor: "var(--mantine-color-gray-3)",
      tickLength: 0,
    },
    yAxis: {
      title: { text: "" },
      min: 0,
      allowDecimals: false,
      gridLineColor: "var(--mantine-color-gray-2)",
      labels: {
        formatter() {
          return axisLabel(Number(this.value), mode, labels)
        },
        style: { color: "var(--mantine-color-gray-7)", fontSize: "13px" },
      },
    },
    tooltip: {
      shared: true,
      useHTML: true,
      outside: true,
      backgroundColor: "var(--mantine-color-body)",
      borderColor: "var(--mantine-color-gray-3)",
      borderRadius: 8,
      shadow: { color: "rgba(15, 23, 42, 0.08)", offsetX: 0, offsetY: 2, width: 6 },
      padding: 10,
      style: { fontSize: "13px", color: "var(--mantine-color-text)" },
      formatter() {
        const ctx = this as unknown as {
          index?: number
          point?: { index?: number }
          points?: Array<{ index?: number; point?: { index?: number } }>
        }
        const i = ctx.points?.[0]?.point?.index ?? ctx.points?.[0]?.index ?? ctx.point?.index ?? ctx.index
        const p = i === undefined ? undefined : points[i]
        if (!p) return false
        const gapAmount = Math.max(0, p.orderAmount - p.deliveredAmount)
        const gapCount = Math.max(0, p.orderCount - p.deliveredCount)
        return `<div style="font-weight:600;margin-bottom:6px">${labels.asOf(p.day)}</div>
          <table style="border-collapse:collapse">
            ${tooltipRow(SERIES.ordered, labels.ordered, p.orderAmount, p.orderCount, labels)}
            ${tooltipRow(SERIES.delivered, labels.delivered, p.deliveredAmount, p.deliveredCount, labels)}
            ${tooltipRow(null, labels.undelivered, gapAmount, gapCount, labels)}
          </table>`
      },
    },
    legend: { enabled: false },
    plotOptions: {
      series: {
        animation: { duration: 300 },
        states: { inactive: { opacity: 1 } },
      },
      line: {
        lineWidth: 2,
        marker: { enabled: false, radius: 4, symbol: "circle", states: { hover: { enabled: true, radius: 5 } } },
        states: { hover: { lineWidthPlus: 0 } },
      },
    },
    series: [
      {
        type: "arearange",
        name: labels.undelivered,
        data: points.map((_, i) => [Math.min(delivered[i], ordered[i]), ordered[i]]),
        color: SERIES.ordered,
        fillOpacity: BAND_OPACITY,
        lineWidth: 0,
        marker: { enabled: false },
        enableMouseTracking: false,
        zIndex: 0,
      },
      {
        type: "line",
        name: labels.delivered,
        color: SERIES.delivered,
        data: withEndDot(delivered, SERIES.delivered),
        zIndex: 1,
      },
      {
        type: "line",
        name: labels.ordered,
        color: SERIES.ordered,
        data: withEndDot(ordered, SERIES.ordered),
        zIndex: 2,
      },
    ],
    accessibility: { enabled: false },
    credits: { enabled: false },
  }
}
